// Layout-canvas geometry — adjacency detection, snapping, and the deduped
// wall takeoff. All coordinates are WORLD FEET (CanvasX/CanvasY store feet,
// not pixels), so zoom never touches the data.
//
// Edge classification between two placed rooms (see detectEdges for the
// facing/parallel/overlap pairing that decides which walls are compared):
//   gap <= SHARED_GAP_FT                    -> 'shared'  (one wall, deduped)
//   SHARED_GAP_FT < gap <= CAVITY_MAX_GAP_FT -> 'cavity' (low-wall return
//     boxout: two parallel walls with an air cavity between — BOTH faces
//     count in the takeoff, plus the cavity itself is tracked)
//   polygons genuinely overlap               -> 'conflict' (drafting error)
//   otherwise -> no edge
// The estimator can override any pair via TakeoffEdgeOverrides
// ('shared' | 'cavity' | 'ignore'); overrides win over geometry.
//
// SHARED_GAP_FT (2026-09-10, widened 0.3 -> 0.6 per direct feedback): the
// real-world margin of error is on EACH room's own wall face independently
// (an estimator tracing/placing a room is accurate to roughly +/-0.3 ft,
// not to a hairline), so two flush rooms can measure up to ~0.6 ft apart
// even when they're actually the same physical wall — a flat 0.3 ft total
// was misclassifying that as a cavity. 0.6 = 0.3 ft tolerance x 2 rooms.
//
// DEFAULT_TUNABLES (2026-09-10): every room-boundary/snap tuning constant
// in this file, collected into one object — the "first-pass heuristic, not
// calibrated against real project data" values below are now editable from
// the Estimate Engine admin page (Takeoff Variables) instead of only ever
// changing via a code deploy. db/65_takeoff_variables.sql seeds this exact
// object; RoomTakeoff.jsx fetches sp_GetTakeoffVariables, merges any
// admin-saved overrides on top of DEFAULT_TUNABLES, and passes the merged
// object as the optional trailing `tunables` argument every function below
// accepts. Every function defaults to DEFAULT_TUNABLES itself, so every
// existing caller that omits the argument — including this file's own test
// suite — behaves identically to before TakeoffVariables existed.
export const DEFAULT_TUNABLES = {
  SHARED_GAP_FT: 0.6,
  CAVITY_MAX_GAP_FT: 2.5,
  CONFLICT_OVERLAP_TOL_FT: 0.3,
  MIN_OVERLAP_FT: 1,      // facing walls must overlap at least this much
  GRID_SNAP_FT: 0.5,
  SNAP_TOL: 0.4,
  WALL_SNAP_TOL: 0.4,
  WALL_SNAP_MIN_OVERLAP: 1,
  NEEDS_REVIEW_FRAC: 0.15,
  SUGGESTED_FRAC: 0.4,
  // Visual depth of the Edge Halo band, independent of CAVITY_MAX_GAP_FT
  // (how far detectEdges searches for a neighbor). The two were one value
  // until 2026-09-10, which made the halo ~30" deep because the search
  // radius is 2.5 ft — the product intent is a ~6" band.
  HALO_DISPLAY_DEPTH_FT: 0.5,
  // Two walls within this angle of each other are parallel; a wall within
  // this angle of an axis is treated as horizontal/vertical. Replaces the old
  // absolute AXIS_TOL_FT endpoint-delta test, which let a short noisy segment
  // at 30° through while rejecting a long wall 1° off.
  PARALLEL_ANGLE_TOL_DEG: 3,
  // Canvas drawing feel (TakeoffCanvas.jsx) — angle-lock step for every
  // click-to-trace tool (22.5° = OST's 16 directions; 90 = plain ortho),
  // vertex snap reach in SCREEN pixels (so precision follows zoom), and how
  // far a door/window symbol reaches to find a wall when placed.
  DRAW_ANGLE_STEP_DEG: 22.5,
  VERTEX_SNAP_PX: 8,
  SYMBOL_WALL_SNAP_FT: 3,
  // Cross-condition overlap check (utils/overlapCheck.js) — how much shared area / length / closeness counts as
  // "the same thing measured twice". Snapped traces share edges, so slivers below these are noise, not findings.
  OVERLAP_MIN_AREA_SF: 0.5,
  OVERLAP_MIN_RUN_FT: 0.5,
  OVERLAP_LINE_TOL_FT: 0.25,
  DUPLICATE_SYMBOL_FT: 0.25,
};

// Individual named exports for the handful of callers that only need one
// constant and don't have a live tunables object handy (e.g. a render call
// site reaching for a band width). These are always DEFAULT_TUNABLES' own
// value — a caller that wants the ADMIN-OVERRIDDEN value must thread a real
// tunables object through instead.
export const SHARED_GAP_FT     = DEFAULT_TUNABLES.SHARED_GAP_FT;
export const CAVITY_MAX_GAP_FT = DEFAULT_TUNABLES.CAVITY_MAX_GAP_FT;
export const MIN_OVERLAP_FT    = DEFAULT_TUNABLES.MIN_OVERLAP_FT;
export const GRID_SNAP_FT      = DEFAULT_TUNABLES.GRID_SNAP_FT;

// Room rect in world feet. CanvasRot 90 swaps footprint dimensions.
export function rectOf(room) {
  if (room.CanvasX === null || room.CanvasX === undefined) return null;
  const rot = room.CanvasRot === 90;
  const w = rot ? +room.WidthFt : +room.LengthFt;
  const h = rot ? +room.LengthFt : +room.WidthFt;
  if (!(w > 0) || !(h > 0)) return null;
  return { x: +room.CanvasX, y: +room.CanvasY, w, h };
}

// A room's real (possibly non-rectangular) boundary, when AI auto-takeoff
// traced one from the drawing's own vector wall lines (db/21_room_takeoff.sql
// PolygonJson; see cs-est-pdf-chunker/room_geometry.py). Returns world-feet
// {x,y} vertices or null — a manually-drawn/edited room has none. Every
// geometry consumer in this file reads it through roomVertices()/
// getRoomEdges() (adjacency, halo, wall takeoff, symbol snap, condition→room
// association); only room drag/resize handles still operate on rectOf()'s
// bounding box (see Platform_Reference.md's explicitly-deferred list).
// AI auto-takeoff's wall-vector tracing (extract_room_polygon(), cs-est-pdf-
// chunker/room_geometry.py) returns Shapely's raw polygonize() output with no
// vertex simplification — nearly-but-not-exactly-collinear extended wall
// lines can intersect a foot or so from a true corner, leaving two vertices
// that were meant to be the same point (confirmed on real production data,
// 2026-09-09 — a stored polygon had corners `{x:249,y:140.7}` and
// `{x:248,y:140.7}`, 1 ft apart, drawing a spurious extra edge/notch into an
// otherwise-clean rectangle). Two conservative passes, applied once:
//   1. merge consecutive vertices closer than MIN_VERTEX_GAP_FT (averaged,
//      not just one dropped, so neither point is arbitrarily favored)
//   2. drop a vertex that's nearly collinear with its own neighbors (not a
//      real corner) — perpendicular distance under COLLINEAR_TOLERANCE_FT
// Both thresholds are comfortably above the observed ~1.0 ft / 0.2 ft noise
// but well below any real minimum corner-to-corner run in these drawings, so
// a genuine short return/reveal is never eaten.
const MIN_VERTEX_GAP_FT = 1.1; // strictly above the observed 1.0 ft real-world case, with margin
const COLLINEAR_TOLERANCE_FT = 0.25;
// Matches perimeterSegments' own axis-alignment tolerance below — used by
// simplifyPolygon's merge to decide whether a surviving neighbor edge counts
// as "vertical" or "horizontal" for the corner-preserving snap (see
// mergeVertexPair's own comment). Widened from an initial 0.05 ft to 0.6 ft
// (2026-09-10) after a second real production case (RoomIDs 271-273) showed
// up to 0.5 ft of end-to-end drift on a wall that's clearly meant to be
// straight — the same "estimator draws to roughly a 0.3 ft margin of error"
// reality already reflected in DEFAULT_TUNABLES.SHARED_GAP_FT (0.6 = two
// independently-imprecise corners each off by up to ~0.3 ft). A wall drifting
// this much over a 20+ ft run is still a fraction of a degree off true and
// nowhere near a genuinely angled wall, so treating it as orthogonal is safe.
const AXIS_TOL_FT = 0.6;

// Merges two near-duplicate consecutive vertices (a, b) into one point.
// A plain midpoint is wrong whenever both surviving neighbor edges (q0->a,
// b->q1) are themselves axis-aligned: averaging a's and b's coordinates
// shifts the merged point off of BOTH lines, turning two clean orthogonal
// walls into one slightly-diagonal edge. That diagonal then fails
// perimeterSegments' own axis-alignment test and becomes invisible to
// detectEdges — confirmed on real production data (RoomID 270, 2026-09-10):
// a 0.5 ft notch at a room's bottom-left corner got averaged away, shifting
// the room's left wall 0.25 ft off-vertical at one end and silently
// deleting an otherwise-obvious shared-wall detection with its neighbor.
// So when q0->a is vertical and b->q1 is horizontal (or vice versa), snap to
// the point that keeps BOTH lines exactly where their OTHER, non-duplicate
// endpoint already puts them — q0's x paired with q1's y, or q1's x paired
// with q0's y — rather than a's/b's own coordinates, which are exactly the
// near-duplicate, possibly-noisy points being discarded. (An earlier version
// of this fix used a's/b's own coordinates instead of q0's/q1's; that
// happened to match here whenever the surviving edge was already perfectly
// clean, which is all the first production case needed, but a genuinely
// noisy neighbor edge — the second production case — exposed the difference:
// using the noisy near point just re-encoded its own error instead of
// discarding it.) Falls back to the original plain average whenever a
// neighbor is unavailable or either surviving edge isn't axis-aligned.
function mergeVertexPair(q0, a, b, q1) {
  if (q0 && q1) {
    const inVert = Math.abs(q0.x - a.x) < AXIS_TOL_FT, inHorz = Math.abs(q0.y - a.y) < AXIS_TOL_FT;
    const outVert = Math.abs(b.x - q1.x) < AXIS_TOL_FT, outHorz = Math.abs(b.y - q1.y) < AXIS_TOL_FT;
    if (inVert && outHorz) return { x: q0.x, y: q1.y };
    if (inHorz && outVert) return { x: q1.x, y: q0.y };
  }
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

export function simplifyPolygon(points) {
  if (!Array.isArray(points) || points.length < 4) return points;

  // Pass 1: merge near-duplicate consecutive vertices (ring-wrapping).
  let pts = [];
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const prev = pts[pts.length - 1];
    if (prev && Math.hypot(p.x - prev.x, p.y - prev.y) < MIN_VERTEX_GAP_FT) {
      const q0 = pts.length > 1 ? pts[pts.length - 2] : null;
      const q1 = points[(i + 1) % points.length];
      pts[pts.length - 1] = mergeVertexPair(q0, prev, p, q1);
    } else {
      pts.push({ x: p.x, y: p.y });
    }
  }
  // First/last can be a near-duplicate pair too (the ring wraps) — merge and
  // drop the last if so.
  if (pts.length > 3 && Math.hypot(pts[0].x - pts[pts.length - 1].x, pts[0].y - pts[pts.length - 1].y) < MIN_VERTEX_GAP_FT) {
    const a = pts[pts.length - 1], b = pts[0];
    const q0 = pts[pts.length - 2], q1 = pts[1];
    pts[0] = mergeVertexPair(q0, a, b, q1);
    pts.pop();
  }
  if (pts.length < 3) return points;

  // Pass 2: drop vertices that sit almost exactly on the line through their
  // own neighbors — not a real corner, just noise along an edge.
  const out = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[(i - 1 + pts.length) % pts.length], b = pts[i], c = pts[(i + 1) % pts.length];
    const abLen = Math.hypot(c.x - a.x, c.y - a.y);
    const perpDist = abLen < 1e-6 ? 0 : Math.abs((c.x - a.x) * (a.y - b.y) - (a.x - b.x) * (c.y - a.y)) / abLen;
    if (perpDist >= COLLINEAR_TOLERANCE_FT) out.push(b);
  }
  return out.length >= 3 ? out : pts;
}

export function polygonOf(room) {
  if (!room?.PolygonJson) return null;
  try {
    const parsed = JSON.parse(room.PolygonJson);
    return Array.isArray(parsed?.points) && parsed.points.length >= 3 ? simplifyPolygon(parsed.points) : null;
  } catch { return null; }
}

const DEG = Math.PI / 180;

// The one authoritative vertex ring for a room: its traced PolygonJson when
// it has one, else the 4 corners of its rectangle. null when unplaced.
export function roomVertices(room) {
  const poly = polygonOf(room);
  if (poly) return poly;
  const R = rectOf(room);
  return R ? [{ x: R.x, y: R.y }, { x: R.x + R.w, y: R.y }, { x: R.x + R.w, y: R.y + R.h }, { x: R.x, y: R.y + R.h }] : null;
}

// A room's perimeter as directed edges — the geometry primitive every
// relationship test builds on, so a rectangle and an L-shaped polygon go
// through identical code (a rectangle is simply 4 edges; a notched room 6).
// Each edge carries its unit tangent and an OUTWARD unit normal, derived
// from the ring's winding (signed shoelace area) so it is correct for either
// vertex order. The outward normal is what lets detectEdges tell a wall that
// FACES a neighbor from one that merely runs parallel to it.
// [{ edgeIndex, roomId, p1, p2, length, tangent, outwardNormal, midpoint }]
export function getRoomEdges(room) {
  const verts = roomVertices(room);
  if (!verts) return [];
  let twiceArea = 0;
  for (let i = 0; i < verts.length; i++) {
    const a = verts[i], b = verts[(i + 1) % verts.length];
    twiceArea += a.x * b.y - b.x * a.y;
  }
  const sign = twiceArea >= 0 ? 1 : -1;
  const edges = [];
  for (let i = 0; i < verts.length; i++) {
    const p1 = verts[i], p2 = verts[(i + 1) % verts.length];
    const dx = p2.x - p1.x, dy = p2.y - p1.y;
    const length = Math.hypot(dx, dy);
    if (length < 1e-6) continue;
    const tangent = { x: dx / length, y: dy / length };
    edges.push({
      edgeIndex: i, roomId: room.RoomID, p1, p2, length, tangent,
      outwardNormal: { x: sign * tangent.y, y: -sign * tangent.x },
      midpoint: { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 },
    });
  }
  return edges;
}

// 'h' | 'v' when the direction is within PARALLEL_ANGLE_TOL_DEG of an axis,
// else null (a genuinely angled wall).
function axisOrientation(tangent, tunables) {
  const sinTol = Math.sin(tunables.PARALLEL_ANGLE_TOL_DEG * DEG);
  if (Math.abs(tangent.y) <= sinTol) return "h";
  if (Math.abs(tangent.x) <= sinTol) return "v";
  return null;
}

// Axis-aligned projection of a room's perimeter edges: { orientation:
// 'h'|'v'|null, fixed, lo, hi } in world feet ('h': fixed=y, lo/hi=x range;
// 'v': fixed=x, lo/hi=y range). Kept for callers that reason in axis terms;
// a genuinely diagonal wall comes back with orientation:null — real
// cleanroom drawings are overwhelmingly orthogonal, and downstream wall
// attribution (computeAutoWallSegments) still works axis-by-axis.
export function perimeterSegments(room, tunables = DEFAULT_TUNABLES) {
  return getRoomEdges(room).map(e => {
    const o = axisOrientation(e.tangent, tunables);
    if (o === "h") return { orientation: "h", fixed: (e.p1.y + e.p2.y) / 2, lo: Math.min(e.p1.x, e.p2.x), hi: Math.max(e.p1.x, e.p2.x) };
    if (o === "v") return { orientation: "v", fixed: (e.p1.x + e.p2.x) / 2, lo: Math.min(e.p1.y, e.p2.y), hi: Math.max(e.p1.y, e.p2.y) };
    return { orientation: null, fixed: null, lo: null, hi: null };
  });
}

// Shoelace formula — true polygon area (closed automatically).
export function polygonArea(points) {
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i], b = points[(i + 1) % points.length];
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

// A room's stored PolygonJson doesn't mean it's actually irregular — AI
// auto-takeoff (sp_ApplyTakeoffAnalysis) traces a room's real boundary from
// the drawing's vector walls and stores it even when that boundary happens
// to be a plain rectangle, so "has a polygon" alone is the wrong test for
// the Rooms dock's "Irregular" label. Compares the polygon's own area
// against its axis-aligned bounding box's area instead — a true rectangle's
// ratio is exactly 1.0; a 2% tolerance forgives the small rounding noise a
// wall-vector trace naturally introduces without needing to inspect point
// count or per-corner angles (more edge cases with collinear/duplicate
// trace points). A genuine notch/L-shape falls well below this threshold.
export function isPolygonRectangular(poly) {
  if (!poly || poly.length < 3) return true;
  const xs = poly.map(p => p.x), ys = poly.map(p => p.y);
  const bboxArea = (Math.max(...xs) - Math.min(...xs)) * (Math.max(...ys) - Math.min(...ys));
  if (!(bboxArea > 0)) return true;
  return polygonArea(poly) / bboxArea >= 0.98;
}

// Sum of edge lengths, closed loop.
export function polygonPerimeter(points) {
  let len = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i], b = points[(i + 1) % points.length];
    len += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return len;
}

export const pairKey = (a, b) => a < b ? `${a}|${b}` : `${b}|${a}`;

// A room-pair wall may carry more than one Edge Run along its length (see
// TakeoffEdgeOverrides.RunKey, db/21_room_takeoff.sql) — this is the one
// place that key format is composed, so the override map (JS state),
// detectEdges()'s lookup, and the save payload never drift out of sync.
// runKey is '' for a pair's first (and, for two plain rectangles, only) run —
// see detectEdges()'s own comment for why that specific backward-compat
// convention matters. Only a genuinely new multi-run pair (polygon rooms
// whose boundary touches a neighbor across more than one segment) produces
// additional, non-empty keys.
export const edgeOverrideKey = (a, b, runKey = "") => `${pairKey(a, b)}::${runKey}`;

// Geometry-anchored Edge Run identity (2026-09-10, replacing a sorted-position
// index). 'H:<fixedY>:<runStartX>' / 'V:<fixedX>:<runStartY>' — the physical
// wall line the run sits on plus where along it the run starts, quantized
// to 1/8 ft so float noise never changes a key. Built from coordinates only,
// so it doesn't depend on polygon winding, vertex order, or which room is
// 'a'. Inserting another run elsewhere on the same wall leaves this key
// alone (the old index would have shifted it); a MATERIAL move of the wall
// or the run's start (> 1/16 ft) intentionally produces a new key, so a
// persisted override never silently migrates to a different physical wall.
export const RUN_KEY_QUANTUM_FT = 0.125;
export function runKeyFor(orientation, fixed, start) {
  const q = v => Math.round(v / RUN_KEY_QUANTUM_FT) * RUN_KEY_QUANTUM_FT;
  return `${orientation === "h" ? "H" : "V"}:${q(fixed)}:${q(start)}`;
}

// Looks an edge up in a map keyed by edgeOverrideKey (overrides, chase
// attributes): its geometric key first, then — for a pair's first run only —
// the legacy '' key every override saved before geometric keys existed was
// stored under. New saves always write the geometric key.
export function edgeLookup(map, edge) {
  const geo = map[edgeOverrideKey(edge.a.RoomID, edge.b.RoomID, edge.runKey)];
  if (geo !== undefined) return geo;
  return edge.legacyRunKey != null ? map[edgeOverrideKey(edge.a.RoomID, edge.b.RoomID, edge.legacyRunKey)] : undefined;
}

// 1-D overlap of [a1,a2] and [b1,b2]
const overlap1d = (a1, a2, b1, b2) => Math.min(a2, b2) - Math.max(a1, b1);

// Y-intervals where a vertical line at x passes through the polygon's
// interior. x must not coincide with a vertex (callers use slab midpoints).
export function yIntervalsAt(verts, x) {
  const ys = [];
  for (let i = 0; i < verts.length; i++) {
    const a = verts[i], b = verts[(i + 1) % verts.length];
    if ((a.x < x) !== (b.x < x)) ys.push(a.y + (b.y - a.y) * (x - a.x) / (b.x - a.x));
  }
  ys.sort((p, q) => p - q);
  const out = [];
  for (let i = 0; i + 1 < ys.length; i += 2) out.push([ys[i], ys[i + 1]]);
  return out;
}

// How much two room polygons genuinely overlap — the real-geometry
// replacement for the old bounding-box conflict test, which false-flagged
// two L-shaped rooms whose boxes overlap but whose walls never do. Sweeps
// the vertical slabs between every distinct x; in each, intersects the two
// polygons' interior y-intervals. xExtent = total slab width with any
// overlap, yExtent = the widest overlap seen in one slab. For two plain
// rectangles that reduces exactly to their x-overlap and y-overlap.
export function polygonOverlapExtents(vertsA, vertsB) {
  const xs = [...new Set([...vertsA, ...vertsB].map(p => p.x))].sort((a, b) => a - b);
  let xExtent = 0, yExtent = 0, area = 0;
  for (let i = 0; i + 1 < xs.length; i++) {
    const w = xs[i + 1] - xs[i];
    if (w < 1e-9) continue;
    const xm = (xs[i] + xs[i + 1]) / 2;
    const ia = yIntervalsAt(vertsA, xm), ib = yIntervalsAt(vertsB, xm);
    let len = 0;
    for (const [a1, a2] of ia) for (const [b1, b2] of ib) len += Math.max(0, Math.min(a2, b2) - Math.max(a1, b1));
    if (len > 1e-9) { xExtent += w; yExtent = Math.max(yExtent, len); area += len * w; }
  }
  return { xExtent, yExtent, area };
}

// Detect Edge Runs between all placed room pairs — the geometry engine's
// factual output. Each room's perimeter comes from getRoomEdges() (polygon
// or rectangle, no difference downstream), and two perimeter edges become a
// candidate pair only when they are PARALLEL (within PARALLEL_ANGLE_TOL_DEG),
// FACE each other (outward normals anti-parallel), sit within
// CAVITY_MAX_GAP_FT along the normal, and PROJECT onto each other by at least
// MIN_OVERLAP_FT. Facing is what stops a wall pairing with a narrow
// neighbor's far wall (two normals pointing the same way) — the phantom
// cavity run that a plain same-orientation + |Δcoordinate| test produced
// for any neighbor narrower than the search radius (a chase or closet drawn
// as a room).
//
// A pair whose polygons genuinely overlap (polygonOverlapExtents beyond
// CONFLICT_OVERLAP_TOL_FT on both axes) yields ONE 'conflict' edge instead
// of wall runs — the geometry is contradictory, so nothing is inferred from
// it until a room is moved.
//
// overrides is keyed by edgeOverrideKey(a, b, runKey). runKey is the
// geometric identity from runKeyFor() — anchored on the LOWER-RoomID room's
// own wall-face line plus the run's start along it, so it is the same
// regardless of pair order or polygon winding and survives another run
// being inserted on the same wall. Lookups fall back to the legacy '' key
// for a pair's first run (see edgeLookup); `legacyRunKey` ('' or null) and
// `overrideLegacy` (true when the override was found under '') let callers
// retire that legacy row when they next save.
//
// Returns [{ a, b, orientation: 'v'|'h', runKey, legacyRunKey, gapFt (magnitude),
//            signedGapFt (+ outside a, − intruding), overlapFt, type, auto,
//            override, overrideLegacy, seg: {x1,y1,x2,y2} (joint centerline),
//            aFixed, bFixed (each room's own wall-face coordinate),
//            edgeAIndex, edgeBIndex, parallelScore, facingScore }]
export function detectEdges(rooms, overrides = {}, tunables = DEFAULT_TUNABLES) {
  const placed = rooms
    .map(r => ({ room: r, rect: rectOf(r), verts: roomVertices(r), edges: getRoomEdges(r) }))
    .filter(x => x.rect);
  const cosTol = Math.cos(tunables.PARALLEL_ANGLE_TOL_DEG * DEG);
  const out = [];
  for (let i = 0; i < placed.length; i++) {
    for (let j = i + 1; j < placed.length; j++) {
      const A = placed[i], B = placed[j];

      const ovl = polygonOverlapExtents(A.verts, B.verts);
      if (ovl.xExtent > tunables.CONFLICT_OVERLAP_TOL_FT && ovl.yExtent > tunables.CONFLICT_OVERLAP_TOL_FT) {
        out.push(conflictEdge(A, B, ovl, overrides));
        continue;
      }

      const runs = [];
      for (const ea of A.edges) {
        const orientation = axisOrientation(ea.tangent, tunables);
        if (!orientation) continue; // genuinely angled wall — still not adjacency-testable downstream
        const t = ea.tangent, n = ea.outwardNormal;
        for (const eb of B.edges) {
          const parallelScore = Math.abs(t.x * eb.tangent.x + t.y * eb.tangent.y);
          if (parallelScore < cosTol) continue;
          const facingScore = -(n.x * eb.outwardNormal.x + n.y * eb.outwardNormal.y);
          if (facingScore < cosTol) continue;

          const b1 = { x: eb.p1.x - ea.p1.x, y: eb.p1.y - ea.p1.y };
          const b2 = { x: eb.p2.x - ea.p1.x, y: eb.p2.y - ea.p1.y };
          const signedGap = ((b1.x * n.x + b1.y * n.y) + (b2.x * n.x + b2.y * n.y)) / 2;
          if (Math.abs(signedGap) > tunables.CAVITY_MAX_GAP_FT) continue;

          const s1 = b1.x * t.x + b1.y * t.y, s2 = b2.x * t.x + b2.y * t.y;
          const sLo = Math.max(0, Math.min(s1, s2)), sHi = Math.min(ea.length, Math.max(s1, s2));
          if (sHi - sLo < tunables.MIN_OVERLAP_FT) continue;

          const along = orientation === "h" ? "x" : "y", perp = orientation === "h" ? "y" : "x";
          const w1 = ea.p1[along] + sLo * t[along], w2 = ea.p1[along] + sHi * t[along];
          const aFixed = (ea.p1[perp] + ea.p2[perp]) / 2, bFixed = (eb.p1[perp] + eb.p2[perp]) / 2;
          runs.push({
            orientation, lo: Math.min(w1, w2), hi: Math.max(w1, w2),
            gap: Math.abs(signedGap), signedGap, mid: (aFixed + bFixed) / 2,
            aFixed, bFixed, parallelScore, facingScore,
            edgeAIndex: ea.edgeIndex, edgeBIndex: eb.edgeIndex,
          });
        }
      }
      if (!runs.length) continue;

      runs.sort((r1, r2) => r1.lo - r2.lo); // legacy '' fallback goes to the first run by position
      const anchorIsA = A.room.RoomID < B.room.RoomID;
      runs.forEach((run, idx) => {
        const auto = run.gap <= tunables.SHARED_GAP_FT ? "shared" : "cavity";
        const runKey = runKeyFor(run.orientation, anchorIsA ? run.aFixed : run.bFixed, run.lo);
        const legacyRunKey = idx === 0 ? "" : null;
        const geoOverride = overrides[edgeOverrideKey(A.room.RoomID, B.room.RoomID, runKey)] || null;
        const legacyOverride = !geoOverride && legacyRunKey != null
          ? overrides[edgeOverrideKey(A.room.RoomID, B.room.RoomID, legacyRunKey)] || null : null;
        const override = geoOverride || legacyOverride;
        const seg = run.orientation === "v"
          ? { x1: run.mid, y1: run.lo, x2: run.mid, y2: run.hi }
          : { x1: run.lo, y1: run.mid, x2: run.hi, y2: run.mid };
        out.push({
          a: A.room, b: B.room, orientation: run.orientation, runKey, legacyRunKey,
          gapFt: run.gap, signedGapFt: run.signedGap, overlapFt: run.hi - run.lo,
          type: override || auto, auto, override, overrideLegacy: !!legacyOverride, seg,
          aFixed: run.aFixed, bFixed: run.bFixed,
          edgeAIndex: run.edgeAIndex, edgeBIndex: run.edgeBIndex,
          parallelScore: run.parallelScore, facingScore: run.facingScore,
        });
      });
    }
  }
  return out;
}

// The single edge emitted for a room pair whose polygons overlap. Its seg is
// the centerline of the overlap region's bounding box along its longer axis
// so the canvas has something to draw and label; gapFt is the penetration
// depth. An estimator may 'ignore' it (silence), nothing else — see
// RoomTakeoff.jsx's cycleEdge.
function conflictEdge(A, B, ovl, overrides) {
  const ix = Math.max(A.rect.x, B.rect.x), ax = Math.min(A.rect.x + A.rect.w, B.rect.x + B.rect.w);
  const iy = Math.max(A.rect.y, B.rect.y), ay = Math.min(A.rect.y + A.rect.h, B.rect.y + B.rect.h);
  const orientation = ax - ix >= ay - iy ? "h" : "v";
  const cx = (ix + ax) / 2, cy = (iy + ay) / 2;
  const penetration = Math.min(ovl.xExtent, ovl.yExtent);
  const override = overrides[edgeOverrideKey(A.room.RoomID, B.room.RoomID, "")] || null;
  return {
    a: A.room, b: B.room, orientation, runKey: "", legacyRunKey: null,
    gapFt: penetration, signedGapFt: -penetration, overlapFt: Math.max(ovl.xExtent, ovl.yExtent),
    overlapArea: ovl.area,
    type: override || "conflict", auto: "conflict", override, overrideLegacy: false,
    seg: orientation === "h" ? { x1: ix, y1: cy, x2: ax, y2: cy } : { x1: cx, y1: iy, x2: cx, y2: ay },
    aFixed: null, bFixed: null,
  };
}

// Confidence tiers for one detected Edge Run, per the vision doc's
// exception-based workflow (Section 18): 'auto_apply' (high confidence, no
// review needed) | 'suggested' (likely, quick one-click confirm) |
// 'needs_review' (ambiguous geometry) | 'conflict' (geometry looks
// contradictory) | 'manual_override' (the estimator already decided — wins
// over everything else). A first-pass heuristic, not calibrated against
// real project data yet — same "reasonable default, correct as reference
// data firms up" status as SHARED_GAP_FT/CAVITY_MAX_GAP_FT themselves.
//
// The geometric tiers are driven by how close the detected gap sits to a
// classification BOUNDARY, as a FRACTION of that classification's own range
// width (not a fixed foot value) — a fixed band would either never fire for
// the narrow 'shared' range (0 to SHARED_GAP_FT) or fire on nearly every
// 'cavity' edge (a much wider range); measuring proportionally means "dead
// center of either range" reads as confident and "near either edge of
// either range" reads as ambiguous, regardless of the range's own width.
export function classifyConfidence(edge, tunables = DEFAULT_TUNABLES) {
  if (edge.override) return "manual_override";
  // detectEdges() decides conflict from the two rooms' real polygon overlap
  // (polygonOverlapExtents, beyond CONFLICT_OVERLAP_TOL_FT on both axes) and
  // emits it as its own edge; a facing pair whose neighbor intrudes by less
  // than that tolerance is ordinary click imprecision and classifies below.
  if (edge.auto === "conflict") return "conflict";

  const { SHARED_GAP_FT, CAVITY_MAX_GAP_FT, NEEDS_REVIEW_FRAC, SUGGESTED_FRAC } = tunables;
  const range = edge.auto === "shared" ? SHARED_GAP_FT : (CAVITY_MAX_GAP_FT - SHARED_GAP_FT);
  const distToBoundary = edge.auto === "shared"
    ? SHARED_GAP_FT - edge.gapFt
    : Math.min(edge.gapFt - SHARED_GAP_FT, CAVITY_MAX_GAP_FT - edge.gapFt);
  const frac = range > 0 ? distToBoundary / range : 1;

  if (frac <= NEEDS_REVIEW_FRAC) return "needs_review";
  if (frac <= SUGGESTED_FRAC) return "suggested";
  return "auto_apply";
}

// Wall takeoff over placed rooms. Heights: shared walls use the taller room;
// cavity walls use each room's own height (both faces count).
// Gross = every room's full perimeter; net removes the dedup on shared walls.
export function wallTakeoff(rooms, edges) {
  const placed = rooms.filter(r => rectOf(r));
  let grossLF = 0, grossSF = 0;
  for (const r of placed) {
    // Real perimeter (polygon when traced, else the rectangle) — the same
    // ring computeAutoWallSegments walks, so the "Gross wall" tile and the
    // per-wall condition rows agree on a notched room.
    const per = polygonPerimeter(roomVertices(r));
    grossLF += per;
    grossSF += per * (+r.HeightFt || 0);
  }

  let sharedLF = 0, sharedSF = 0, sharedRemovedSF = 0;
  let cavityPlanLF = 0, cavityWallSF = 0, cavityPairs = 0;
  for (const e of edges) {
    if (e.type === "ignore") continue;
    const hA = +e.a.HeightFt || 0, hB = +e.b.HeightFt || 0;
    if (e.type === "shared") {
      sharedLF += e.overlapFt;
      sharedSF += e.overlapFt * Math.max(hA, hB);
      // gross counted both faces; net keeps one wall at the taller height
      sharedRemovedSF += e.overlapFt * Math.min(hA, hB);
    } else if (e.type === "cavity") {
      cavityPairs += 1;
      cavityPlanLF += e.overlapFt;
      cavityWallSF += e.overlapFt * (hA + hB); // both faces stay in the takeoff
    }
  }

  return {
    placedCount: placed.length,
    grossLF, grossSF,
    sharedLF, sharedSF,
    cavityPairs, cavityPlanLF, cavityWallSF,
    netWallSF: grossSF - sharedRemovedSF,
    netWallLF: grossLF - sharedLF,
  };
}

// Snap a dragged room. Magnetic targets (flush against a neighbor, or an
// exact 1 ft cavity gap) are evaluated from the RAW drop position and win;
// the 0.5 ft grid is the fallback — grid rounding must never push a position
// out of magnetic tolerance. (SNAP_TOL/WALL_SNAP_TOL/WALL_SNAP_MIN_OVERLAP
// now live in DEFAULT_TUNABLES above.)

// Snaps a dragged room's edges to the DRAWING's own real wall lines
// ({hSegs, vSegs} in world feet — see VITE_WALL_SEGMENTS_URL /
// cs-est-takeoff-api/wall-segments, extracted server-side from the PDF's
// own vector linework) ahead of the room-to-room magnetic targets below.
// Ground truth beats approximation: two rooms that each independently snap
// to the SAME real wall line end up genuinely flush (gap ~0 ft), which is
// also what correctly auto-classifies as a 'shared' joint instead of a
// 'cavity' from a few inches of placement slop (see detectEdges'
// SHARED_GAP_FT) — the actual motivation for this function existing.
// wallSegments is undefined for a raster/scanned or uncalibrated sheet
// (nothing to snap to) or simply not yet fetched; every existing caller
// that omits it behaves identically to before this existed.
function wallSnapTargets(wallSegments, moving, x, y, tunables) {
  const { WALL_SNAP_TOL, WALL_SNAP_MIN_OVERLAP } = tunables;
  let nx = null, ny = null, bestX = WALL_SNAP_TOL + 1, bestY = WALL_SNAP_TOL + 1;
  for (const { x: wx, y0, y1 } of wallSegments.vSegs || []) {
    if (Math.min(moving.y + moving.h, y1) - Math.max(moving.y, y0) < WALL_SNAP_MIN_OVERLAP) continue;
    for (const target of [wx, wx - moving.w]) { // room's left face on this wall, or its right face
      const d = Math.abs(x - target);
      if (d <= WALL_SNAP_TOL && d < bestX) { bestX = d; nx = target; }
    }
  }
  for (const { y: wy, x0, x1 } of wallSegments.hSegs || []) {
    if (Math.min(moving.x + moving.w, x1) - Math.max(moving.x, x0) < WALL_SNAP_MIN_OVERLAP) continue;
    for (const target of [wy, wy - moving.h]) { // room's top face on this wall, or its bottom face
      const d = Math.abs(y - target);
      if (d <= WALL_SNAP_TOL && d < bestY) { bestY = d; ny = target; }
    }
  }
  return { nx, ny };
}

// xSource/ySource on the return value ('wall' | 'room' | 'grid' | 'free')
// identify which tier resolved each axis — the Layout Canvas shows this live
// next to the cursor while dragging (see TakeoffCanvas.jsx's drag-feedback
// label) so an estimator can see they've actually hit a real wall/room edge
// and aren't just settling on the coarse 0.5 ft grid fallback.
//
// disableSnap (2026-09-09, per direct feedback — a wall-snap candidate that's
// simply wrong, e.g. traced from a non-wall line in the source PDF, had no
// escape hatch: the wall tier always wins over the room/grid tiers with no
// way to reject it, so a bad candidate near the drop point made it
// impossible to place the room anywhere else no matter how precisely the
// estimator aimed) — when true, skip every tier and return the raw
// position untouched. TakeoffCanvas.jsx wires this to the Alt key.
export function snapPosition(room, x, y, rooms, wallSegments, disableSnap = false, tunables = DEFAULT_TUNABLES) {
  const moving = rectOf({ ...room, CanvasX: x, CanvasY: y });
  if (disableSnap) return { x, y, xSource: "free", ySource: "free" };
  let nx = null, ny = null, xSource = null, ySource = null;

  if (moving && wallSegments) {
    const wall = wallSnapTargets(wallSegments, moving, x, y, tunables);
    if (wall.nx !== null) { nx = wall.nx; xSource = "wall"; }
    if (wall.ny !== null) { ny = wall.ny; ySource = "wall"; }
  }

  if (moving && (nx === null || ny === null)) {
    const { SNAP_TOL, MIN_OVERLAP_FT } = tunables;
    let bestX = SNAP_TOL + 1, bestY = SNAP_TOL + 1;
    for (const other of rooms) {
      if (other.RoomID === room.RoomID) continue;
      const O = rectOf(other);
      if (!O) continue;

      const yOv = overlap1d(y, y + moving.h, O.y, O.y + O.h);
      if (nx === null && yOv >= MIN_OVERLAP_FT) {
        for (const target of [O.x - moving.w, O.x + O.w, O.x - moving.w - 1, O.x + O.w + 1]) {
          const d = Math.abs(x - target);
          if (d <= SNAP_TOL && d < bestX) { bestX = d; nx = target; xSource = "room"; }
        }
      }
      const xOv = overlap1d(x, x + moving.w, O.x, O.x + O.w);
      if (ny === null && xOv >= MIN_OVERLAP_FT) {
        for (const target of [O.y - moving.h, O.y + O.h, O.y - moving.h - 1, O.y + O.h + 1]) {
          const d = Math.abs(y - target);
          if (d <= SNAP_TOL && d < bestY) { bestY = d; ny = target; ySource = "room"; }
        }
      }
    }
  }

  if (nx === null) { nx = Math.round(x / tunables.GRID_SNAP_FT) * tunables.GRID_SNAP_FT; xSource = "grid"; }
  if (ny === null) { ny = Math.round(y / tunables.GRID_SNAP_FT) * tunables.GRID_SNAP_FT; ySource = "grid"; }
  return { x: nx, y: ny, xSource, ySource };
}

// The resize-drag equivalent of snapPosition() — snaps ONE edge coordinate
// along one axis, not a whole room's (x, y) position, since resizing only
// ever moves one axis per edge (a corner handle calls this twice, once per
// axis). Added 2026-09-10: resizing a room's edge previously computed
// w/h/x/y purely from the raw cursor position and only grid-rounded to the
// nearest 0.5 ft on release, with no awareness of a neighboring room's
// actual wall face at all — unlike a whole-room drag, which already ran
// through snapPosition()'s magnetic wall/room tiers. That let a resized
// edge land up to ~0.5 ft off a neighbor that wasn't itself grid-aligned
// (e.g. one placed via a magnetic snap target that isn't a multiple of
// 0.5 ft), read by an estimator as "I dragged it right up to the wall and
// it still shows a gap." Same tier order and tolerances as snapPosition()
// (wall line > neighboring room edge > 0.5 ft grid > raw, with Alt to
// disable), just scoped to one coordinate.
// overlapRange is the room's own (fixed) extent along the OTHER axis —
// used the same way snapPosition() checks yOv/xOv, since two rooms only
// "face" each other if they actually overlap along the perpendicular axis.
export function snapEdge(axis, value, overlapRange, rooms, wallSegments, disableSnap = false, tunables = DEFAULT_TUNABLES) {
  if (disableSnap) return { value, source: "free" };
  const [lo, hi] = overlapRange;

  if (wallSegments) {
    const segs = axis === "x" ? (wallSegments.vSegs || []) : (wallSegments.hSegs || []);
    let best = tunables.WALL_SNAP_TOL + 1, target = null;
    for (const seg of segs) {
      const segLo = axis === "x" ? seg.y0 : seg.x0, segHi = axis === "x" ? seg.y1 : seg.x1;
      if (Math.min(hi, segHi) - Math.max(lo, segLo) < tunables.WALL_SNAP_MIN_OVERLAP) continue;
      const t = axis === "x" ? seg.x : seg.y;
      const d = Math.abs(value - t);
      if (d <= tunables.WALL_SNAP_TOL && d < best) { best = d; target = t; }
    }
    if (target !== null) return { value: target, source: "wall" };
  }

  let best = tunables.SNAP_TOL + 1, target = null;
  for (const other of rooms) {
    const O = rectOf(other);
    if (!O) continue;
    const oLo = axis === "x" ? O.y : O.x, oHi = axis === "x" ? O.y + O.h : O.x + O.w;
    if (overlap1d(lo, hi, oLo, oHi) < tunables.MIN_OVERLAP_FT) continue;
    for (const t of axis === "x" ? [O.x, O.x + O.w] : [O.y, O.y + O.h]) {
      const d = Math.abs(value - t);
      if (d <= tunables.SNAP_TOL && d < best) { best = d; target = t; }
    }
  }
  if (target !== null) return { value: target, source: "room" };

  return { value: Math.round(value / tunables.GRID_SNAP_FT) * tunables.GRID_SNAP_FT, source: "grid" };
}

// Distance from point p to the segment a-b, and the closest point on it.
export function projectOntoSegment(p, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  const t = l2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2)) : 0;
  const q = { x: a.x + t * dx, y: a.y + t * dy };
  return { point: q, dist: Math.hypot(p.x - q.x, p.y - q.y) };
}

// Ray-casting point-in-polygon.
export function pointInPolygon(p, verts) {
  let inside = false;
  for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) {
    const a = verts[i], b = verts[j];
    if ((a.y > p.y) !== (b.y > p.y) && p.x < a.x + (b.x - a.x) * (p.y - a.y) / (b.y - a.y)) inside = !inside;
  }
  return inside;
}

// Room(s) a drawn polyline runs along — ties a Condition Library placement
// back to the room(s) whose wall it traces, so takeoffs can roll up room by
// room, not just by cost code. Each segment's midpoint is tested against
// every room's real perimeter edges (getRoomEdges — a notched room's inner
// faces count, not just its bounding box) within grid-snap tolerance, since
// drawn points are already corner/grid-snapped; the room hit by the most
// segments wins as the primary association. A condition against a shared
// wall can legitimately touch two rooms — roomIds carries all of them,
// primaryRoomId (the one actually stored) picks one so the schema stays
// simple for now.
export function roomsAlongPolyline(points, rooms) {
  const tol = GRID_SNAP_FT + 0.05;
  const hits = new Map(); // RoomID -> segment hit count
  for (let i = 1; i < points.length; i++) {
    const m = { x: (points[i - 1].x + points[i].x) / 2, y: (points[i - 1].y + points[i].y) / 2 };
    for (const r of rooms) {
      if (getRoomEdges(r).some(e => projectOntoSegment(m, e.p1, e.p2).dist < tol)) {
        hits.set(r.RoomID, (hits.get(r.RoomID) || 0) + 1);
      }
    }
  }
  if (!hits.size) return { primaryRoomId: null, roomIds: [] };
  const roomIds = [...hits.keys()];
  const primaryRoomId = roomIds.reduce((best, id) => hits.get(id) > hits.get(best) ? id : best, roomIds[0]);
  return { primaryRoomId, roomIds };
}

// Snap a click to the nearest room wall — used to place a door/window symbol
// (a Condition Library Style='count' placement) directly onto a wall rather
// than at an arbitrary point. Projects the click onto every placed room's
// real perimeter edges (getRoomEdges — so a door can sit on a notched room's
// inner face, not just its bounding box) and keeps the closest within
// tolerance. wallNormalDeg is the direction "into the room" from that wall
// (the default door-swing direction; 0 = +x, 90 = +y, 180 = −x, −90 = −y —
// the same values the old four-sides version produced, now derived from the
// edge's outward normal); angleDeg is the wall's own orientation folded to
// [0, 180) (0 = horizontal, 90 = vertical) for rendering the symbol along it.
const RAD2DEG = 180 / Math.PI;
// Where two rooms share a wall, both perimeters pass within a hair of the click, so "nearest" alone could hand the
// door to the neighbour on the far side. Candidates within SHARED_WALL_PICK_FT of the nearest are therefore
// tie-broken by which room the click is INSIDE — the door belongs to (and swings into) the room it was drawn in.
// A click outside every room keeps the plain nearest wall, exactly as before.
const SHARED_WALL_PICK_FT = 0.75;
export function snapToNearestWall(x, y, rooms, tunables = DEFAULT_TUNABLES) {
  const cands = [];
  for (const r of rooms) {
    for (const e of getRoomEdges(r)) {
      const { point, dist } = projectOntoSegment({ x, y }, e.p1, e.p2);
      if (dist > tunables.SYMBOL_WALL_SNAP_FT) continue;
      const raw = Math.atan2(e.tangent.y, e.tangent.x) * RAD2DEG;
      const angleDeg = Math.round((((raw % 180) + 180) % 180) * 1000) / 1000;
      const wallNormalDeg = Math.round(Math.atan2(-e.outwardNormal.y, -e.outwardNormal.x) * RAD2DEG * 1000) / 1000;
      cands.push({ x: point.x, y: point.y, angleDeg, wallNormalDeg, roomId: r.RoomID, dist, room: r });
    }
  }
  if (!cands.length) return null;
  const nearest = cands.reduce((a, b) => (b.dist < a.dist ? b : a));
  const inside = cands
    .filter(c => c.dist <= nearest.dist + SHARED_WALL_PICK_FT)
    .filter(c => { const v = roomVertices(c.room); return v && pointInPolygon({ x, y }, v); })
    .reduce((a, b) => (!a || b.dist < a.dist ? b : a), null);
  const { room, ...pick } = inside || nearest;
  return pick;
}

// Which room a drawn AREA polygon sits inside (centroid-in-room test) — used
// to associate a Condition Library Style='area' fill with its room. Distinct
// from roomsAlongPolyline() above, which tests "does this polyline run along
// a wall" (linear conditions); an area fill is inside a room, not along its
// edge, so centroid containment fits better than wall-proximity matching.
// Tests against the room's real polygon (roomVertices), so a fill drawn in
// the notch OUTSIDE an L-shaped room no longer gets attributed to it.
export function roomContainingPolygon(points, rooms) {
  if (!points?.length) return null;
  let cx = 0, cy = 0;
  for (const p of points) { cx += p.x; cy += p.y; }
  const c = { x: cx / points.length, y: cy / points.length };
  for (const r of rooms) {
    const verts = roomVertices(r);
    if (verts && pointInPolygon(c, verts)) return r.RoomID;
  }
  return null;
}

// Turns a detectEdges() result into the wall-type TakeoffConditionsDrawn segment
// list sp_SyncAutoWallConditions upserts — the "marriage" of room-adjacency
// intelligence (shared-wall dedup, cavity both-faces, gap classification)
// with the Condition Library's cost-coded/priced condition records, instead
// of requiring every wall to be manually re-traced. Mirrors wallTakeoff()'s
// own classification exactly (same edges, same 'ignore' skip, same taller-
// room-height rule for a shared wall) so the two numbers reconcile.
//
// Ordered, corner-connected wall segments for one full walk around a room's
// perimeter, each with its cumulative start-distance ("ring position") and
// direction of travel — used only by the exterior-remainder gap-merging
// below, so an uncovered stretch that spans a corner (e.g. a room's whole
// unattached side, or two whole sides with nothing between them) comes out
// as ONE continuous gap instead of splitting at every corner regardless of
// whether anything actually changed there. Unlike perimeterSegments() (order-
// independent, used by detectEdges), traversal order/direction matter here.
function ringWalk(room) {
  const verts = roomVertices(room);
  if (!verts) return [];
  let ringStart = 0;
  return verts.map((p1, i) => {
    const p2 = verts[(i + 1) % verts.length];
    const len = Math.hypot(p2.x - p1.x, p2.y - p1.y);
    const seg = Math.abs(p1.y - p2.y) < 0.05
      ? { orientation: "h", fixed: p1.y, lo: Math.min(p1.x, p2.x), hi: Math.max(p1.x, p2.x), dir: p2.x >= p1.x ? 1 : -1 }
      : Math.abs(p1.x - p2.x) < 0.05
      ? { orientation: "v", fixed: p1.x, lo: Math.min(p1.y, p2.y), hi: Math.max(p1.y, p2.y), dir: p2.y >= p1.y ? 1 : -1 }
      : { orientation: null, fixed: null, lo: null, hi: null, dir: null };
    const out = { ...seg, ringStart, len };
    ringStart += len;
    return out;
  });
}

// One segment per SHARED edge (one physical wall — RoomID is the lower
// RoomID, RoomBID the higher, height = the taller room's; counted once
// project-wide, shown under both rooms via RoomBID — see
// sp_GetConditionRoomRollup), two segments per CAVITY edge (one per room,
// each room's own height — both faces kept), and one segment per genuinely
// distinct "exterior/independent" gap in a room's own perimeter (its own
// perimeter LF minus whatever's covered by a shared/cavity edge touching it
// — still a lump quantity per gap, no exact drawable points, since it isn't
// one single traced run). A room with no neighbors at all, or one whole
// unattached stretch, still produces exactly ONE such gap — gaps only split
// where an actual neighbor (or two neighbors with a step between them)
// creates a real discontinuity, so a plain room's line-item count is
// unchanged from before this existed. `key` is the stable diff key
// sp_SyncAutoWallConditions matches on to preserve a manually-assigned
// TakeoffConditionLibraryID across a layout change; each room's first (and,
// for the common single-gap case, only) exterior gap keeps the exact same
// bare `ext:<roomId>` key it always has, so an already-assigned exterior
// condition type survives unchanged — only an ADDITIONAL gap beyond the
// first (new information the old single lump couldn't represent) gets a
// position-suffixed key.
// Picks the Construction Standard rule for one Edge Run out of the rules
// configured for its room-template pair (db/59_construction_standard_rules.sql).
// `rules` is an array of { ruleId, kind, itemId, assemblyId, relationshipType,
// distanceMinFt, distanceMaxFt, priority } (a bare single object is accepted
// for older callers). A rule matches when each of its selectors is NULL or
// satisfied: relationshipType against the run's type, the inclusive gap
// range against gapFt. Highest priority wins; two DIFFERENT entities tied at
// the top are ambiguous and resolve to null — the wall stays blank for a
// human rather than the engine guessing.
export function matchingConstructionRules(edge, rules) {
  if (!rules) return [];
  const list = Array.isArray(rules) ? rules : [rules];
  const eps = 1e-6;
  return list
    .filter(r =>
      (r.relationshipType == null || r.relationshipType === edge.type) &&
      (r.distanceMinFt == null || edge.gapFt >= r.distanceMinFt - eps) &&
      (r.distanceMaxFt == null || edge.gapFt <= r.distanceMaxFt + eps))
    .sort((p, q) => (q.priority ?? 100) - (p.priority ?? 100));
}

export function resolveConstructionRule(edge, rules) {
  const matches = matchingConstructionRules(edge, rules);
  if (!matches.length) return null;
  const top = matches[0].priority ?? 100;
  const best = matches.filter(r => (r.priority ?? 100) === top);
  const entity = r => `${r.kind}:${r.kind === "item" ? r.itemId : r.assemblyId}`;
  return new Set(best.map(entity)).size > 1 ? null : best[0];
}

// rulesByPair (Room Boundary Intelligence — Construction Standard rules,
// see project memory): an optional Map, keyed by pairKey(RoomTemplateAID,
// RoomTemplateBID), value = the array of rules for that pair (see
// resolveConstructionRule) — sp_GetConstructionStandardRules results
// reshaped by the caller (RoomTakeoff.jsx). When present, a 'shared'/'cavity'
// edge classified 'auto_apply' by classifyConfidence() (the tier meant to
// need no review — see that function's own comment) and whose two rooms'
// templates have a matching rule gets that rule's Kind/ItemID/AssemblyID
// attached to its segment(s); sp_SyncAutoWallConditions
// (db/37_condition_library.sql) only ever uses this to fill a wall that's
// CURRENTLY unassigned, never to override an existing pick. Omitted
// entirely (stays null) for every other tier, and for 'ext' (exterior)
// segments, which have no "other room" to pair against. Backward
// compatible — every existing call site (including this file's own test
// suite) omits the 3rd arg and gets identical behavior.
//
// opts.wallSystemByRoom (2026-09-11 — "some rooms are 2\" wall panel, this
// one is gypsum"): a Map RoomID -> { kind, itemId, assemblyId } giving each
// room's EFFECTIVE wall system (its own TakeoffRooms.Wall* substitution, else
// its Room Template's default — resolved by the caller, RoomTakeoff.jsx).
// Used only where no pair rule decided: an exterior segment takes its room's
// system (no confidence gate — an exterior wall isn't an inferred
// relationship, it's the room's own wall); an auto_apply shared wall takes
// it only when BOTH rooms resolve to the same entity — two different
// systems meeting (panel vs. gypsum) is the estimator's call, so the wall
// stays blank and the Review Walls chooser offers both; each auto_apply
// cavity face takes its own room's. Pair rules (db/59) always win over this
// fallback. Omitted = identical behavior to before.
export function sameWallSystem(a, b) {
  if (!a || !b || a.kind !== b.kind) return false;
  return a.kind === "item" ? a.itemId === b.itemId : a.assemblyId === b.assemblyId;
}

// Wall systems BY POSITION (2026-10-14). A room / template may name a different
// wall system for each kind of wall it has: 'exterior' (the room's own wall, no
// neighbor), 'shared' (one wall two rooms stand on) and 'cavity' (this room's own
// face of a two-wall cavity). Stored as JSON on TakeoffRooms.WallSystemsJson /
// RoomTemplates.WallSystemsJson: { exterior: {kind, id}, shared: {…}, cavity: {…} },
// each key optional; the older WallKind/WallItemID/WallAssemblyID stays the
// "all walls" system a position entry wins over.
export const WALL_POSITIONS = ["exterior", "shared", "cavity"];

/** JSON text (or object) -> { exterior?, shared?, cavity? } with { kind, itemId, assemblyId }; bad entries dropped. */
export function parseWallSystemsJson(json) {
  let obj = json;
  if (typeof json === "string") { try { obj = JSON.parse(json); } catch { return {}; } }
  const out = {};
  if (!obj || typeof obj !== "object") return out;
  for (const p of WALL_POSITIONS) {
    const e = obj[p];
    if (!e || (e.kind !== "item" && e.kind !== "assembly")) continue;
    const id = +e.id;
    if (!(id > 0)) continue;
    out[p] = { kind: e.kind, itemId: e.kind === "item" ? id : null, assemblyId: e.kind === "assembly" ? id : null };
  }
  return out;
}

/** { exterior?, shared?, cavity? } (parsed shape) -> JSON text for the save payload; null when empty. */
export function encodeWallSystems(systems) {
  const o = {};
  for (const p of WALL_POSITIONS) {
    const s = systems?.[p];
    if (s?.kind === "item" && s.itemId) o[p] = { kind: "item", id: +s.itemId };
    else if (s?.kind === "assembly" && s.assemblyId) o[p] = { kind: "assembly", id: +s.assemblyId };
  }
  return Object.keys(o).length ? JSON.stringify(o) : null;
}

/**
 * The wall system a room uses for ONE position. `entry` is a wallSystemByRoom value: either a plain
 * system { kind, itemId, assemblyId } (the "all walls" system — every caller before positions existed)
 * or one that also carries `byPosition` (already resolved in effectiveWallSystems' precedence).
 */
export function wallSystemForPosition(entry, position) {
  if (!entry) return null;
  if (entry.byPosition) return entry.byPosition[position] || null;
  return entry.kind ? entry : null;
}

/**
 * A room's wall system for each position. Precedence, most specific first:
 *   room's own system for the position > room's "all walls" system >
 *   template's system for the position > template's "all walls" default > none.
 * Returns { all, byPosition: { exterior, shared, cavity }, hasPositions } — `all` is exactly what
 * effectiveWallSystem() returns (kept for its existing callers); each resolved system carries
 * `source` ('room' | 'template') and `scope` ('position' | 'all') so the UI can say where it came from.
 */
export function effectiveWallSystems(room, templatesById) {
  const t = room?.RoomTemplateID != null && templatesById ? (templatesById instanceof Map ? templatesById.get(room.RoomTemplateID) : templatesById[room.RoomTemplateID]) : null;
  const roomPos = parseWallSystemsJson(room?.WallSystemsJson);
  const tplPos = parseWallSystemsJson(t?.WallSystemsJson);
  const all = effectiveWallSystem(room, templatesById);
  const tag = (s, source, scope) => (s ? { ...s, source, scope, ...(source === "template" && t ? { templateName: t.Name } : {}) } : null);
  const roomAll = all && all.source === "room" ? all : null;
  const tplAll = all && all.source === "template" ? all : null;
  const byPosition = {};
  for (const p of WALL_POSITIONS) {
    byPosition[p] = tag(roomPos[p], "room", "position") || (roomAll && { ...roomAll, scope: "all" }) || tag(tplPos[p], "template", "position") || (tplAll && { ...tplAll, scope: "all" }) || null;
  }
  return { all, byPosition, hasPositions: WALL_POSITIONS.some(p => roomPos[p] || tplPos[p]) };
}

export function computeAutoWallSegments(rooms, edges, rulesByPair = null, tunables = DEFAULT_TUNABLES, opts = {}) {
  const segments = [];
  const coveredRangesByRoom = new Map(); // RoomID -> [{orientation, fixed, lo, hi}]
  const wallSystemByRoom = opts.wallSystemByRoom || null;
  // opts.predictWall (2026-10-26, wall learning — utils/learning/wallLearning.js): (info) => { kind, itemId,
  // assemblyId, samples, agreement, ... } | null, where info = { rel, gapFt, tplA, tplB }. Asked LAST — only for
  // a wall no pair rule and no wall system decided, and only for a joint the engine is at least 'suggested' about
  // (an exterior wall has no joint to doubt). A prediction is machine output like a rule fill: the sync stores it
  // 'unreviewed', so it prices but is flagged for a person. Omitted = identical behavior to before.
  const predictWall = opts.predictWall || null;

  const heightOf = (room) => (room?.HeightFt != null && room.HeightFt !== "" ? +room.HeightFt : null);
  const addCoveredRange = (roomId, orientation, fixed, lo, hi) => {
    if (!coveredRangesByRoom.has(roomId)) coveredRangesByRoom.set(roomId, []);
    coveredRangesByRoom.get(roomId).push({ orientation, fixed, lo, hi });
  };
  // The room's wall system for a position ('exterior' | 'shared' | 'cavity'); a map entry without
  // per-position data is the single "all walls" system, as before positions existed.
  const wallOf = (roomId, position) => wallSystemForPosition(wallSystemByRoom && wallSystemByRoom.get(roomId), position);
  const asFields = (w, source) => ({ kind: w?.kind ?? null, itemId: w?.itemId ?? null, assemblyId: w?.assemblyId ?? null, ruleId: null, ...(w ? { source } : {}) });
  const ruleFor = (e) => {
    if (!rulesByPair || e.a.RoomTemplateID == null || e.b.RoomTemplateID == null) return null;
    if (classifyConfidence(e, tunables) !== "auto_apply") return null;
    return resolveConstructionRule(e, rulesByPair.get(pairKey(e.a.RoomTemplateID, e.b.RoomTemplateID)));
  };
  // Wall-system fallback for a shared wall: both rooms must agree.
  const sharedWallSystemFor = (e) => {
    if (!wallSystemByRoom || classifyConfidence(e, tunables) !== "auto_apply") return null;
    const wa = wallOf(e.a.RoomID, "shared"), wb = wallOf(e.b.RoomID, "shared");
    if (wa && wb) return sameWallSystem(wa, wb) ? wa : null;
    return wa || wb || null;
  };
  const cavityFaceWallSystemFor = (e, roomId) => {
    if (!wallSystemByRoom || classifyConfidence(e, tunables) !== "auto_apply") return null;
    return wallOf(roomId, "cavity");
  };
  // What the engine knew about the wall (stored on the row for learning — db/37 AutoWall*) plus, for a wall nothing
  // else decided, the learned pre-fill.
  const withFacts = (fields, info, tier) => {
    let f = fields;
    if (f.kind == null && predictWall && (info.rel === "exterior" || tier === "auto_apply" || tier === "suggested")) {
      const p = predictWall(info);
      if (p) f = { kind: p.kind ?? null, itemId: p.itemId ?? null, assemblyId: p.assemblyId ?? null, ruleId: null, source: "learned", learned: { samples: p.samples, agreement: p.agreement, projects: p.projects, basis: p.basis } };
    }
    return { ...f, rel: info.rel, gapFt: info.gapFt ?? null, tier: tier ?? null, tplA: info.tplA ?? null, tplB: info.tplB ?? null };
  };

  for (const e of edges) {
    if (e.type !== "shared" && e.type !== "cavity") continue; // 'ignore', and a 'conflict' (overlapping rooms — nothing to infer)
    const points = [{ x: e.seg.x1, y: e.seg.y1 }, { x: e.seg.x2, y: e.seg.y2 }];
    const [covLo, covHi] = e.orientation === "v" ? [e.seg.y1, e.seg.y2] : [e.seg.x1, e.seg.x2];
    addCoveredRange(e.a.RoomID, e.orientation, e.aFixed, covLo, covHi);
    addCoveredRange(e.b.RoomID, e.orientation, e.bFixed, covLo, covHi);

    // The AutoWallKey suffix is the run's geometric identity (runKeyFor), so
    // an assigned wall row keeps its assignment when another run is inserted
    // on the same wall. `legacyKey` is the bare pre-geometric key a pair's
    // first run used to carry — sp_SyncAutoWallConditions renames an
    // existing row from it to the new key once, so nothing already assigned
    // in production is lost on the first sync after this shipped.
    const runSuffix = `:${e.runKey}`;
    const legacySuffix = e.legacyRunKey === "" ? "" : null;
    const rule = ruleFor(e);
    const tier = classifyConfidence(e, tunables);
    const infoA = { rel: e.type, gapFt: e.gapFt, tplA: e.a.RoomTemplateID ?? null, tplB: e.b.RoomTemplateID ?? null };
    const infoB = { ...infoA, tplA: infoA.tplB, tplB: infoA.tplA };
    const ruleFields = rule
      ? { kind: rule.kind ?? null, itemId: rule.itemId ?? null, assemblyId: rule.assemblyId ?? null, ruleId: rule.ruleId ?? null, source: "rule" }
      : null;
    if (e.type === "shared") {
      const lo = Math.min(e.a.RoomID, e.b.RoomID), hi = Math.max(e.a.RoomID, e.b.RoomID);
      const hA = heightOf(e.a), hB = heightOf(e.b);
      segments.push({
        key: `shared:${lo}:${hi}${runSuffix}`, legacyKey: legacySuffix != null ? `shared:${lo}:${hi}` : null,
        roomId: lo, roomBId: hi,
        lengthFt: e.overlapFt, heightFt: hA == null && hB == null ? null : Math.max(hA || 0, hB || 0),
        points, ...withFacts(ruleFields || asFields(sharedWallSystemFor(e), "wall_system"), infoA, tier),
      });
    } else if (e.type === "cavity") {
      segments.push({ key: `cavity:${e.a.RoomID}:${e.b.RoomID}${runSuffix}`, legacyKey: legacySuffix != null ? `cavity:${e.a.RoomID}:${e.b.RoomID}` : null, roomId: e.a.RoomID, roomBId: null, lengthFt: e.overlapFt, heightFt: heightOf(e.a), points, ...withFacts(ruleFields || asFields(cavityFaceWallSystemFor(e, e.a.RoomID), "wall_system"), infoA, tier) });
      segments.push({ key: `cavity:${e.b.RoomID}:${e.a.RoomID}${runSuffix}`, legacyKey: legacySuffix != null ? `cavity:${e.b.RoomID}:${e.a.RoomID}` : null, roomId: e.b.RoomID, roomBId: null, lengthFt: e.overlapFt, heightFt: heightOf(e.b), points, ...withFacts(ruleFields || asFields(cavityFaceWallSystemFor(e, e.b.RoomID), "wall_system"), infoB, tier) });
    }
  }

  for (const r of rooms) {
    const R = rectOf(r);
    if (!R) continue;
    const ring = ringWalk(r);
    const total = ring.reduce((s, seg) => s + seg.len, 0);
    const hasDiagonal = ring.some(seg => !seg.orientation);
    const covered = coveredRangesByRoom.get(r.RoomID) || [];

    if (hasDiagonal) {
      // Fallback for a room with any non-orthogonal wall: keep the original
      // single-lump behavior. Positioning gaps needs to attribute coverage
      // to a specific wall, which general-angle geometry doesn't support yet
      // (see perimeterSegments()'s own comment) — a genuinely uncoverable
      // ring position here would silently misreport a gap's length otherwise.
      const totalCovered = covered.reduce((s, c) => s + (c.hi - c.lo), 0);
      const remaining = total - totalCovered;
      if (remaining > 0.01) segments.push({ key: `ext:${r.RoomID}`, roomId: r.RoomID, roomBId: null, lengthFt: remaining, heightFt: heightOf(r), points: null, ...withFacts(asFields(wallOf(r.RoomID, "exterior"), "wall_system"), { rel: "exterior", gapFt: null, tplA: r.RoomTemplateID ?? null, tplB: null }, null) });
      continue;
    }

    // Map each covered world-space range onto this room's ring-space
    // (0..total), matching by orientation AND fixed coordinate — matching by
    // lo/hi range alone would confuse two opposite walls that happen to
    // share the same range (e.g. a rectangle's left and right walls both
    // spanning its full height).
    const ringRanges = [];
    for (const c of covered) {
      const seg = ring.find(s => s.orientation === c.orientation && Math.abs(s.fixed - c.fixed) < 0.1 && s.lo - 0.05 <= c.lo && c.hi <= s.hi + 0.05);
      if (!seg) continue; // no matching wall on this room (shouldn't happen for a well-formed pair, but don't crash on it)
      const subLo = Math.max(seg.lo, c.lo), subHi = Math.min(seg.hi, c.hi);
      if (subHi <= subLo) continue;
      const ringLo = seg.dir === 1 ? seg.ringStart + (subLo - seg.lo) : seg.ringStart + (seg.hi - subHi);
      ringRanges.push({ lo: ringLo, hi: ringLo + (subHi - subLo) });
    }
    ringRanges.sort((a, b) => a.lo - b.lo);
    // Merge overlapping/adjacent covered ranges (defensive — shouldn't
    // normally overlap for a valid layout, but a merge here is cheap and safe).
    const merged = [];
    for (const rr of ringRanges) {
      const last = merged[merged.length - 1];
      if (last && rr.lo <= last.hi + 0.01) last.hi = Math.max(last.hi, rr.hi);
      else merged.push({ ...rr });
    }

    const gaps = [];
    if (merged.length === 0) {
      gaps.push({ startsAt: 0, len: total }); // no neighbors at all — the whole ring is one gap, same as the old lump
    } else {
      for (let i = 0; i < merged.length; i++) {
        const cur = merged[i];
        const nextLo = i < merged.length - 1 ? merged[i + 1].lo : merged[0].lo + total; // wraparound for the last gap
        const len = nextLo - cur.hi;
        if (len > 0.01) gaps.push({ startsAt: cur.hi % total, len });
      }
    }

    gaps.sort((a, b) => a.startsAt - b.startsAt);
    gaps.forEach((g, idx) => {
      const key = idx === 0 ? `ext:${r.RoomID}` : `ext:${r.RoomID}:${Math.round(g.startsAt * 10)}`;
      segments.push({ key, roomId: r.RoomID, roomBId: null, lengthFt: g.len, heightFt: heightOf(r), points: null, ...withFacts(asFields(wallOf(r.RoomID, "exterior"), "wall_system"), { rel: "exterior", gapFt: null, tplA: r.RoomTemplateID ?? null, tplB: null }, null) });
    });
  }

  return segments;
}

// A room's effective wall system: its own substitution (TakeoffRooms.Wall*)
// else its Room Template's default (RoomTemplates.Wall*); null when neither.
// `templatesById` = Map/object RoomTemplateID -> template row.
export function effectiveWallSystem(room, templatesById) {
  if (room?.WallKind && (room.WallKind === "item" ? room.WallItemID : room.WallAssemblyID)) {
    return { kind: room.WallKind, itemId: room.WallKind === "item" ? +room.WallItemID : null, assemblyId: room.WallKind === "assembly" ? +room.WallAssemblyID : null, source: "room" };
  }
  const t = room?.RoomTemplateID != null && templatesById ? (templatesById instanceof Map ? templatesById.get(room.RoomTemplateID) : templatesById[room.RoomTemplateID]) : null;
  if (t?.WallKind && (t.WallKind === "item" ? t.WallItemID : t.WallAssemblyID)) {
    return { kind: t.WallKind, itemId: t.WallKind === "item" ? +t.WallItemID : null, assemblyId: t.WallKind === "assembly" ? +t.WallAssemblyID : null, source: "template", templateName: t.Name, name: t.WallName || null, code: t.WallCode || null };
  }
  return null;
}

// Snap a clicked/drawn point to the nearest "hard" geometry within tolFt —
// existing room corners (real polygon vertices), the drawing's own wall-line
// endpoints and horizontal×vertical wall intersections (2-D targets: the
// point moves onto them), else the nearest wall LINE (1-D: only the
// perpendicular coordinate moves). Nothing within tolerance → the raw point.
// tolFt is meant to be derived from SCREEN pixels (px / pxPerFt) so that
// zoomed in, a point placed precisely on a corner stays exactly there, while
// zoomed out the same few pixels forgive a coarser click.
export function snapDrawnPoint(pt, rooms, wallSegments, tolFt) {
  let best = null;
  const consider = (x, y, kind) => {
    const d = Math.hypot(pt.x - x, pt.y - y);
    if (d <= tolFt && (!best || d < best.d)) best = { x, y, kind, d };
  };
  for (const r of rooms) for (const v of roomVertices(r) || []) consider(v.x, v.y, "corner");
  const hs = wallSegments?.hSegs || [], vs = wallSegments?.vSegs || [];
  const nearH = hs.filter(h => Math.abs(h.y - pt.y) <= tolFt && pt.x >= Math.min(h.x0, h.x1) - tolFt && pt.x <= Math.max(h.x0, h.x1) + tolFt);
  const nearV = vs.filter(v => Math.abs(v.x - pt.x) <= tolFt && pt.y >= Math.min(v.y0, v.y1) - tolFt && pt.y <= Math.max(v.y0, v.y1) + tolFt);
  for (const h of nearH) { consider(h.x0, h.y, "wall-end"); consider(h.x1, h.y, "wall-end"); }
  for (const v of nearV) { consider(v.x, v.y0, "wall-end"); consider(v.x, v.y1, "wall-end"); }
  for (const h of nearH) for (const v of nearV) consider(v.x, h.y, "wall-corner");
  if (best) return { x: best.x, y: best.y, kind: best.kind };

  let line = null;
  for (const h of nearH) { const d = Math.abs(h.y - pt.y); if (!line || d < line.d) line = { x: pt.x, y: h.y, d }; }
  for (const v of nearV) { const d = Math.abs(v.x - pt.x); if (!line || d < line.d) line = { x: v.x, y: pt.y, d }; }
  if (line) return { x: line.x, y: line.y, kind: "wall-line" };
  return { x: pt.x, y: pt.y, kind: null };
}

// The next corner of a traced outline with the angle locked: the pointer's
// RAW direction from the previous corner is rounded to the nearest stepDeg
// increment (OST-style polar lock — 22.5° gives 16 directions, 90° gives
// plain ortho), and the snapped candidate then contributes only its
// component ALONG that locked ray. A nearby snap target can therefore slide
// the point along the intended line but never tilt it off the chosen angle
// — the old "snap after lock" order let a corner a foot to the side turn a
// clearly-vertical wall into a 5° one. Returns the point plus the locked
// angle in degrees (0 = +x, 90 = +y).
export function orthoSnap(prev, raw, snapped, stepDeg = 90) {
  const step = stepDeg * DEG;
  const angle = Math.round(Math.atan2(raw.y - prev.y, raw.x - prev.x) / step) * step;
  const dx = Math.cos(angle), dy = Math.sin(angle);
  const along = (snapped.x - prev.x) * dx + (snapped.y - prev.y) * dy;
  const angleDeg = Math.round(angle / DEG * 1000) / 1000;
  return { x: prev.x + dx * along, y: prev.y + dy * along, angleDeg, axis: angleDeg % 180 === 0 ? "h" : Math.abs(angleDeg) % 180 === 90 ? "v" : null };
}

// Bounding box of everything placed (for fit-to-view and auto-place)
export function layoutBounds(rooms) {
  const rects = rooms.map(rectOf).filter(Boolean);
  if (!rects.length) return null;
  return {
    minX: Math.min(...rects.map(r => r.x)),
    minY: Math.min(...rects.map(r => r.y)),
    maxX: Math.max(...rects.map(r => r.x + r.w)),
    maxY: Math.max(...rects.map(r => r.y + r.h)),
  };
}

// ── Takeoff Canvas 2.0 Track C — polygon validity ────────────────────────────
// A traced or edited outline is checked BEFORE it is committed: too few
// corners, a zero-area sliver, coincident consecutive corners, or a bow-tie
// (self-intersecting) outline are refused with a reason the canvas shows —
// never silently stored and never "fixed" by re-interpreting the shape.
// Rectangles and L / U notches pass exactly as before.
const orient = (a, b, c) => Math.sign((b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x));
function segmentsCross(p1, p2, p3, p4) {
  const o1 = orient(p1, p2, p3), o2 = orient(p1, p2, p4), o3 = orient(p3, p4, p1), o4 = orient(p3, p4, p2);
  if (o1 === 0 || o2 === 0 || o3 === 0 || o4 === 0) return false; // touching / collinear — not a proper crossing
  return o1 !== o2 && o3 !== o4;
}
/**
 * Tidy the clicked points of a trace before it is finished. A double-click (the usual way to finish) delivers TWO clicks, and
 * the second can land a hair away from the first — or snap to a neighbouring target — leaving an extra corner a few pixels
 * from the last one, which then makes the outline cross itself. So:
 *   - a point within `tolFt` of the previous kept point is dropped (the second click of the double-click);
 *   - for a CLOSED shape, a last point within `tolFt` of the first is dropped too (the click that closes the loop on the
 *     starting corner is "close", not another corner).
 * `tolFt` is meant to be a few screen pixels (px / pxPerFt), so the tolerance follows the zoom.
 */
export function cleanTracePoints(points, { tolFt = 0.01, closed = false } = {}) {
  const tol = Math.max(0.01, +tolFt || 0);
  const out = [];
  for (const p of points || []) {
    const last = out[out.length - 1];
    if (!last || Math.hypot(p.x - last.x, p.y - last.y) > tol) out.push(p);
  }
  if (closed && out.length > 2 && Math.hypot(out[out.length - 1].x - out[0].x, out[out.length - 1].y - out[0].y) <= tol) out.pop();
  return out;
}
export function polygonSelfIntersects(points) {
  const n = points?.length || 0;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (j === i + 1 || (i === 0 && j === n - 1)) continue; // adjacent edges share a corner
      if (segmentsCross(points[i], points[(i + 1) % n], points[j], points[(j + 1) % n])) return true;
    }
  }
  return false;
}
export function polygonValidity(points, { minEdgeFt = 0.05 } = {}) {
  const reasons = [];
  if (!Array.isArray(points) || points.length < 3) return { ok: false, reasons: ["too_few_points"] };
  for (let i = 0; i < points.length; i++) {
    const a = points[i], b = points[(i + 1) % points.length];
    if (Math.hypot(a.x - b.x, a.y - b.y) < minEdgeFt) { reasons.push("duplicate_vertices"); break; }
  }
  if (polygonArea(points) < 0.01) reasons.push("zero_area");
  if (polygonSelfIntersects(points)) reasons.push("self_intersecting");
  return { ok: reasons.length === 0, reasons };
}
// Remove one corner; refuses to go below a triangle or to produce an invalid outline.
export function removePolygonVertex(points, index) {
  if (!Array.isArray(points) || index < 0 || index >= points.length) return { ok: false, reason: "index_out_of_range", points };
  if (points.length <= 3) return { ok: false, reason: "too_few_points", points };
  const next = points.filter((_, i) => i !== index);
  const v = polygonValidity(next);
  return v.ok ? { ok: true, points: next } : { ok: false, reason: v.reasons[0], points };
}
// Bounds of the PLACED rooms in a list (real polygons included) — null when none.
export function boundsOfRooms(rooms) {
  const placed = (rooms || []).filter(r => r && r.CanvasX != null && r.CanvasX !== "" && r.CanvasY != null && r.CanvasY !== "");
  return placed.length ? layoutBounds(placed) : null;
}
